<script setup lang="ts">
  import { CallsService, type SuperUserCall as Call } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type Filter,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { Badge } from '@/components/ui/badge'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<Call>> => {
    const filters = options?.filters || []
    const durationRangeFilter = filters.find((filter) => filter.key === 'duration')?.value
    const humanRatingFilter = filters.find((filter) => filter.key === 'humanRating')?.value
    const aiRatingFilter = filters.find((filter) => filter.key === 'rating')?.value
    const data = {
      durationRange: durationRangeFilter,
      humanRatings: humanRatingFilter,
      aiRatings: aiRatingFilter
    }

    return CallsService.callsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      durationRanges: data.durationRange,
      humanRatings: data.humanRatings,
      aiRatings: data.aiRatings
    }) as unknown as PaginatedResponse<unknown>
  }

  const filterableFields: Filter[] = [
    {
      key: 'duration',
      label: 'Duration',
      type: 'select',
      options: [
        { label: 'Less than 20s', value: '0-20' },
        { label: '20 - 59 seconds', value: '20-59' },
        { label: '1 - 3 minutes', value: '60-180' },
        { label: 'More than 3 minutes', value: '180-660' }
      ]
    },
    {
      key: 'humanRating',
      label: 'Rating by Human',
      type: 'multi-select',
      options: [
        { label: 'No rating', value: 'None' },
        { label: '1 rating', value: '1' },
        { label: '2 rating', value: '2' },
        { label: '3 rating', value: '3' },
        { label: '4 rating', value: '4' },
        { label: '5 rating', value: '5' }
      ]
    },
    {
      key: 'rating',
      label: 'Rating by AI',
      type: 'multi-select',
      options: [
        { label: 'No rating', value: 'None' },
        { label: '1 rating', value: '1' },
        { label: '2 rating', value: '2' },
        { label: '3 rating', value: '3' },
        { label: '4 rating', value: '4' },
        { label: '5 rating', value: '5' }
      ]
    }
  ]
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Calls', to: ROUTES.ADMIN.VOICE.CALL_LIST }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  :fetchData="fetchCalls"
                  searchableFields="UUID, phone numbers or customer name"
                  :filterableFields="filterableFields"
                  @update-filters="($event) => console.log($event)"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Datetime</TableCell>
                    <TableCell>From phone number</TableCell>
                    <TableCell>To phone number</TableCell>
                    <TableCell>Classification</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Rated</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: calls }: { items: Call[] }">
                    <TableRow
                      v-for="call in calls"
                      :key="call.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', (call as Call).uuid)"
                        >
                          {{ call.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        {{
                          call.call_start_time ? formatDateTimeGerman(call.call_start_time) : '-'
                        }}
                      </TableCell>
                      <TableCell>
                        {{ call.user_phone_number }}
                      </TableCell>
                      <TableCell>
                        {{ call.phone_number.phone_number }}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {{
                            call.result?.classification
                              ? (call as Call).result?.classification
                              : '-'
                          }}
                        </Badge>
                      </TableCell>
                      <CustomerTableDataCell :org="call.phone_number.org" />
                      <TableCell>
                        {{ call.duration ? call.duration + 's' : '-' }}
                      </TableCell>
                      <TableCell>
                        <Badge :variant="call.is_rated ? 'default' : 'secondary'">
                          {{ call.is_rated ? 'Rated' : 'Not Rated' }}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge
                                class="cursor-pointer"
                                :variant="'secondary'"
                              >
                                {{ call.system_rating ? call.system_rating : '-' }}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent v-if="call.system_rating">Rated by AI</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge
                                class="ml-3 cursor-pointer"
                                :variant="'default'"
                              >
                                {{
                                  call.system_rating_by_human ? call.system_rating_by_human : '-'
                                }}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent v-if="call.system_rating_by_human">
                              Rated by Human
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <router-link
                          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                            text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50
                            dark:text-blue-500 dark:hover:text-blue-400"
                          :to="ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', call.uuid)"
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
